import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStep} from "../workflow-step";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {BulkOrderDesignService} from "./bulk-order-design.service";
import {BulkOrderLineItemModel} from "../../bulk-ordering/bulk-order-line-item.model";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {get} from "lodash";
import {SearchHelpers} from "../../helpers/search-helper";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./pick-devices-bulk-order.template.pug",
  providers: [BulkOrderDesignService],
})
@exposeComponentInDebugMode()
export class PickDevicesBulkOrderComponent extends WorkflowStep {
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  searchText: string = "";

  catalogPricingEnabled: boolean = false;
  private devices: BulkOrderLineItemModel[] = [];
  filteredDevices: BulkOrderLineItemModel[] = [];
  displayedDevices: BulkOrderLineItemModel[] = [];
  disableStep: boolean = false;

  error: Error;

  @handleErrors()
  async autoSave(): Promise<boolean> {
    const data = this.devices
      .filter(device => device.quantity !== device.originalQuantity)
      .map(device => ({
        deviceId: device.deviceId,
        deviceKey: device.deviceKey,
        productNumber: device.productNumber,
        quantity: device.quantity,
        type: device.type,
      }));

    if (!data.length) {
      return true;
    }

    const result = await this.bulkOrderDesignService.saveDesign(this.workflowTargetInfo.targetId, data);

    if (result) {
      await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
    }

    return result;
  }

  @handleErrors()
  async ready() {
    this.loading = false;

    if (this.isLocked) {
      this.disableStep = true;
    }

    if (get(this, "workflowService.workflowConfig.target.orderRequested") as boolean) {
      this.disableStep = true;
    }

    await this.loadData();
  }

  checkDirty(): boolean {
    return this.devices.some(it => it.quantity !== it.originalQuantity);
  }

  @handleErrors()
  private async loadData(): Promise<any> {
    const rows = await this.bulkOrderDesignService.getDeviceList(this.workflowTargetInfo.targetId);

    for (const row of rows) {
      row.originalQuantity = row.quantity;
      row.disabled = !row.catalogPricingEnabled && row.maxQuantity <= 0;
    }

    this.catalogPricingEnabled = rows.length && rows[0].catalogPricingEnabled;
    this.devices = rows;

    this.recalculateMaxPages();
    this.loadList();
  }

  updateQuantity(device: BulkOrderLineItemModel): void {
    let quantity = device.quantity;
    const maxQuantity = device.quantity;

    if (quantity < 0) {
      quantity = 0;
      device.quantity = quantity;
    }

    if (typeof maxQuantity === "number" && (maxQuantity < quantity)) { // ignore null/undefined
      quantity = maxQuantity;
      device.quantity = quantity;
    }

    device.isInvalid = !device.disabled && !this.disableStep &&
      !Number.isNaN(maxQuantity) && quantity > maxQuantity;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.loadList();
  }

  private loadList(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.filteredDevices = this.devices.filter((asset) => {
      if (this.searchText) {
        const values = [
          asset.id,
          asset.deviceId,
          asset.deviceKey,
          asset.productNumber,
          asset.productDescription,
          asset.type,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedDevices = this.filteredDevices.slice(start, end);
    this.recalculateMaxPages();
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filteredDevices.length / (+this.pageSize));
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private bulkOrderDesignService: BulkOrderDesignService,
  ) {
    super(true);
  }
}
